import { useCallback, useState } from 'react';
import { AccessType } from '@/types/property.types';

/** Minimal shape of the form values the hook validates. */
export interface AccessFormValues {
  accessType: string | null;
  accessCode: string | null;
  lockboxLocation: string | null;
  rentlyRegistrationPin: string | null;
  contactName: string | null;
  contactNumber: string | null;
  additionalNotes: string | undefined;
  firstAvailableDateStatus: string;
  firstAvailableDate: string | null;
}

export type FormErrors = Record<string, string>;

// ---------------------------------------------------------------------------
// Standalone validators
// ---------------------------------------------------------------------------

const NAME_PATTERN = /^[a-zA-Z.,' -]*$/;

function validateContactNumber(input: string): boolean {
  const numbers = input.replace(/\D/g, '');
  return (
    (numbers.length === 10 && !numbers.startsWith('1')) ||
    (numbers.length === 11 && numbers.startsWith('1'))
  );
}

function validateAccessCodeLength(code: string | null): boolean {
  if (!code) return false;
  return code.length >= 3 && code.length <= 15;
}

// ---------------------------------------------------------------------------
// Core validation
// ---------------------------------------------------------------------------

export function validateForm(values: AccessFormValues): FormErrors {
  const errors: FormErrors = {};

  // --- access type ---
  if (!values.accessType) {
    errors.accessType = 'Please select access mode';
  }

  // --- per-access-type fields ---
  if (values.accessType === AccessType.LOCKBOX) {
    if (!values.lockboxLocation) {
      errors.lockboxLocation = 'Please enter lockbox location';
    }
    if (!validateAccessCodeLength(values.accessCode)) {
      errors.accessCode =
        'Please insert alphanumeric code between 3 and 15 characters';
    }
  }

  if (values.accessType === AccessType.KEYPAD) {
    if (!validateAccessCodeLength(values.accessCode)) {
      errors.accessCode =
        'Please insert alphanumeric code between 3 and 15 characters';
    }
  }

  if (values.accessType === AccessType.RENTLY) {
    if (!values.rentlyRegistrationPin) {
      errors.rentlyRegistrationPin =
        'Please enter valid registration pin between 3 and 15 digits';
    }
  }

  // --- contact info ---
  if (
    !values.contactName ||
    values.contactName === '' ||
    !NAME_PATTERN.test(values.contactName)
  ) {
    errors.contactName = 'Please enter valid contact name';
  }

  if (!values.contactNumber) {
    errors.contactNumber = 'Please enter valid contact number';
  } else if (!validateContactNumber(values.contactNumber)) {
    errors.contactNumber = 'Please enter valid contact number';
  }

  // --- scheduling ---
  if (
    values.firstAvailableDateStatus !== 'Yes' &&
    !values.firstAvailableDate
  ) {
    errors.firstAvailableDate = 'Please select valid date';
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAccessFormValidation() {
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = useCallback((values: AccessFormValues): boolean => {
    const result = validateForm(values);
    setErrors(result);
    return Object.keys(result).length === 0;
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  return { errors, validate, clearFieldError, clearAllErrors };
}
